import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStores, createStore, updateStore, deleteStore, getUsers } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { UkAddressFields, formatAddress } from '../../components/UkAddressFields';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Warehouse, MapPin, User as UserIcon, ChevronRight, Pencil, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

// `kind` is the second-class citizen here — it changes the billing rule for
// any work-order parts pulled from this store. Customer stores hold stock
// the customer already paid for, so consumed parts are tracked but never
// added to the draft Xero invoice.
const empty = { name: '', address: '', manager: '', kind: 'lutrom', customerId: '' };
const NO_CUSTOMER = '__none__';

const Stores = () => {
  const [stores, setStores] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const refresh = () => Promise.all([
    getStores(),
    getUsers(),
  ]).then(([s, u]) => {
    setStores(s);
    setCustomers(u.filter(x => x.role === 'customer'));
  });
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const customerById = Object.fromEntries(customers.map(c => [c.id, c]));

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      address: s.address || '',
      manager: s.manager || '',
      kind: s.kind || 'lutrom',
      customerId: s.customerId || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Store name required');
    if (form.kind === 'customer' && !form.customerId) return toast.error('Customer store needs a linked customer');
    try {
      const payload = {
        name: form.name.trim(),
        address: form.address.trim(),
        manager: form.manager.trim(),
        kind: form.kind,
        customerId: form.kind === 'customer' ? form.customerId : null,
      };
      if (editing) {
        await updateStore(editing.id, payload);
        toast.success('Store updated');
      } else {
        await createStore(payload);
        toast.success('Store created');
      }
      setOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}? This removes all its locations.`)) return;
    try {
      await deleteStore(s.id);
      toast.success('Store deleted');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Inventory Stores"
        description="Warehouses, vans and customer-owned stock locations. Lutrom stores feed draft invoices; customer stores don't."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="new-store-btn">
                <Plus className="h-4 w-4 mr-2" /> New Store
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? 'Edit Store' : 'Create Store'}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Type</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, kind: 'lutrom', customerId: '' })}
                      data-testid="store-kind-lutrom"
                      className={`text-left p-3 rounded-md border-2 transition-colors ${
                        form.kind === 'lutrom'
                          ? 'border-slate-900 bg-slate-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Warehouse className="h-4 w-4 text-slate-700" />
                        <span className="font-medium text-slate-900">Lutrom store</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        Our own stock — used parts go on the draft invoice.
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, kind: 'customer' })}
                      data-testid="store-kind-customer"
                      className={`text-left p-3 rounded-md border-2 transition-colors ${
                        form.kind === 'customer'
                          ? 'border-amber-600 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="h-4 w-4 text-amber-700" />
                        <span className="font-medium text-slate-900">Customer store</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        Customer's own stock — used parts are tracked but not invoiced.
                      </div>
                    </button>
                  </div>
                </div>
                {form.kind === 'customer' && (
                  <div>
                    <Label>Customer</Label>
                    <Select
                      value={form.customerId || NO_CUSTOMER}
                      onValueChange={(v) => setForm({ ...form, customerId: v === NO_CUSTOMER ? '' : v })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="store-customer-select">
                        <SelectValue placeholder="Pick a customer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_CUSTOMER}>— Pick a customer —</SelectItem>
                        {customers.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.company || c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Name</Label>
                  <Input data-testid="store-name-input" className="mt-1.5" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Lutrom Store" />
                </div>
                <div>
                  <Label>Address</Label>
                  <div className="mt-1.5">
                    <UkAddressFields
                      value={form.addressParts}
                      legacyText={form.address}
                      onChange={(parts) => setForm((f) => ({ ...f, addressParts: parts, address: formatAddress(parts, ', ') }))}
                      testId="store-address-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Manager</Label>
                  <Input data-testid="store-manager-input" className="mt-1.5" value={form.manager} onChange={e => setForm({ ...form, manager: e.target.value })} placeholder="Person responsible" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-store-btn">{editing ? 'Save changes' : 'Create store'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stores.map(s => {
          const isCustomer = s.kind === 'customer';
          const linkedCustomer = isCustomer && customerById[s.customerId];
          return (
            <div
              key={s.id}
              data-testid={`store-card-${s.id}`}
              className={`bg-white border rounded-md p-5 hover:shadow-md transition-all ${
                isCustomer ? 'border-amber-200 hover:border-amber-400' : 'border-slate-200 hover:border-blue-700'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-md text-white flex items-center justify-center ${
                    isCustomer ? 'bg-amber-700' : 'lutrom-metal'
                  }`}>
                    {isCustomer ? <Building2 className="h-5 w-5" /> : <Warehouse className="h-5 w-5" />}
                  </div>
                  <div>
                    <div className="text-xs font-mono text-slate-500 flex items-center gap-1.5">
                      {s.id}
                      <span
                        className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                          isCustomer ? 'bg-amber-100 text-amber-900' : 'bg-slate-200 text-slate-700'
                        }`}
                        data-testid={`store-kind-badge-${s.id}`}
                      >
                        {isCustomer ? 'Customer' : 'Lutrom'}
                      </span>
                    </div>
                    <h3 className="font-display uppercase text-lg text-slate-900 tracking-wide mt-0.5">{s.name}</h3>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-store-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={`delete-store-${s.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                {linkedCustomer && (
                  <div className="flex items-center gap-2 text-amber-800">
                    <Building2 className="h-4 w-4 shrink-0" />
                    {linkedCustomer.company || linkedCustomer.name}
                  </div>
                )}
                {s.address && <div className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 shrink-0" /> {s.address}</div>}
                {s.manager && <div className="flex items-center gap-2 text-slate-600"><UserIcon className="h-4 w-4 shrink-0" /> {s.manager}</div>}
              </div>
              <Link to={`/admin/stores/${s.id}`} className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-sm font-medium text-blue-800 hover:text-blue-900" data-testid={`open-store-${s.id}`}>
                Manage locations <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          );
        })}
        {stores.length === 0 && (
          <div className="col-span-full p-12 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-md">
            No stores yet. Create your first store to start organising inventory.
          </div>
        )}
      </div>
    </div>
  );
};

export default Stores;
